import { demoConversionResolvedEmitter, demoConversionStartedEmitter } from "@Context/common/demo/demo-events";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { NOTIFICATION_SWITCH_DELAY_MS } from "./notification-service.constants";
import { INotificationCandidate, INotificationRuntimeContext } from "./notification-service.types";

const NotificationRuntimeContext = createContext<INotificationRuntimeContext | null>(null);

interface INotificationRuntimeCandidate extends INotificationCandidate {
  isReady: boolean;
}

const getNextCandidate = (candidates: Record<string, INotificationRuntimeCandidate>, isQueueBlocked: boolean) => {
  if (isQueueBlocked) {
    return null;
  }

  return (
    Object.values(candidates)
      .filter(candidate => candidate.isActive && candidate.isReady)
      .sort((left, right) => left.priority - right.priority)[0] ?? null
  );
};

interface INotificationRuntimeProvider {
  children: ReactNode;
}

export const NotificationRuntimeProvider = ({ children }: INotificationRuntimeProvider) => {
  const [candidates, setCandidates] = useState<Record<string, INotificationRuntimeCandidate>>({});
  const [activeCandidateId, setActiveCandidateId] = useState<string | null>(null);
  const [isQueueBlocked, setIsQueueBlocked] = useState(false);
  const switchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const candidateDelayTimeoutsRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearCandidateDelayTimeout = useCallback((id: string) => {
    const existingTimeout = candidateDelayTimeoutsRef.current[id];

    if (!existingTimeout) {
      return;
    }

    clearTimeout(existingTimeout);
    delete candidateDelayTimeoutsRef.current[id];
  }, []);

  const upsertCandidate = useCallback((candidate: INotificationCandidate) => {
    setCandidates(currentCandidates => {
      const currentCandidate = currentCandidates[candidate.id];
      const shouldDelayActivation = candidate.isActive && (candidate.delayMs ?? 0) > 0;
      const shouldResetReadiness = !currentCandidate || currentCandidate.isActive !== candidate.isActive || currentCandidate.delayMs !== candidate.delayMs;

      if (
        currentCandidate?.priority === candidate.priority &&
        currentCandidate.isActive === candidate.isActive &&
        currentCandidate.delayMs === candidate.delayMs &&
        currentCandidate.render === candidate.render
      ) {
        return currentCandidates;
      }

      return {
        ...currentCandidates,
        [candidate.id]: {
          ...candidate,
          isReady: shouldDelayActivation ? (shouldResetReadiness ? false : currentCandidate.isReady) : candidate.isActive,
        },
      };
    });
  }, []);

  const removeCandidate = useCallback((id: string) => {
    clearCandidateDelayTimeout(id);

    setCandidates(currentCandidates => {
      if (!currentCandidates[id]) {
        return currentCandidates;
      }

      const nextCandidates = { ...currentCandidates };
      delete nextCandidates[id];

      return nextCandidates;
    });
  }, [clearCandidateDelayTimeout]);

  useEffect(() => {
    Object.entries(candidateDelayTimeoutsRef.current).forEach(([candidateId]) => {
      const candidate = candidates[candidateId];
      const shouldDelayActivation = candidate?.isActive && (candidate.delayMs ?? 0) > 0 && !candidate.isReady;

      if (!shouldDelayActivation) {
        clearCandidateDelayTimeout(candidateId);
      }
    });

    Object.values(candidates).forEach(candidate => {
      const shouldDelayActivation = candidate.isActive && (candidate.delayMs ?? 0) > 0;

      if (!shouldDelayActivation) {
        return;
      }

      if (candidate.isReady || candidateDelayTimeoutsRef.current[candidate.id]) {
        return;
      }

      candidateDelayTimeoutsRef.current[candidate.id] = setTimeout(() => {
        setCandidates(currentCandidates => {
          const currentCandidate = currentCandidates[candidate.id];

          if (!currentCandidate?.isActive || currentCandidate.isReady) {
            return currentCandidates;
          }

          return {
            ...currentCandidates,
            [candidate.id]: {
              ...currentCandidate,
              isReady: true,
            },
          };
        });

        delete candidateDelayTimeoutsRef.current[candidate.id];
      }, candidate.delayMs);
    });
  }, [candidates, clearCandidateDelayTimeout]);

  const nextCandidate = useMemo(() => {
    return getNextCandidate(candidates, isQueueBlocked);
  }, [candidates, isQueueBlocked]);

  useEffect(() => {
    if (switchTimeoutRef.current) {
      clearTimeout(switchTimeoutRef.current);
      switchTimeoutRef.current = null;
    }

    if (nextCandidate?.id === activeCandidateId) {
      return;
    }

    if (nextCandidate && activeCandidateId) {
      switchTimeoutRef.current = setTimeout(() => {
        setActiveCandidateId(nextCandidate.id);
        switchTimeoutRef.current = null;
      }, NOTIFICATION_SWITCH_DELAY_MS);

      return;
    }

    setActiveCandidateId(nextCandidate?.id ?? null);
  }, [activeCandidateId, nextCandidate]);

  useEffect(() => {
    const unsubscribeConversionStarted = demoConversionStartedEmitter.on(() => {
      setIsQueueBlocked(true);
    });

    const unsubscribeConversionResolved = demoConversionResolvedEmitter.on(() => {
      setIsQueueBlocked(false);
    });

    return () => {
      unsubscribeConversionStarted();
      unsubscribeConversionResolved();

      if (switchTimeoutRef.current) {
        clearTimeout(switchTimeoutRef.current);
      }

      Object.keys(candidateDelayTimeoutsRef.current).forEach(clearCandidateDelayTimeout);
    };
  }, [clearCandidateDelayTimeout]);

  const activeCandidate = activeCandidateId ? (candidates[activeCandidateId] ?? null) : null;

  const value = useMemo(() => {
    return {
      activeCandidate,
      removeCandidate,
      upsertCandidate,
    };
  }, [activeCandidate, removeCandidate, upsertCandidate]);

  return <NotificationRuntimeContext.Provider value={value}>{children}</NotificationRuntimeContext.Provider>;
};

export const useNotificationRuntime = () => {
  const context = useContext(NotificationRuntimeContext);

  if (!context) {
    throw new Error("useNotificationRuntime must be used within NotificationRuntimeProvider");
  }

  return context;
};
